import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import axios from 'axios';
import { isUserLicensed } from '@bb/users';
import AuthContext from './AuthContext';
import { getOngoingTransferCount } from '../api/UserController';
import {
  OrgStatus,
  ORG_STATUS_ORDER,
  StageName,
  STAGE_ORDER,
} from '../Router/onboarding/constants';
import { SecretsController } from '../api';

export type AdminContextType = {
  queueCount: number;
  ongoingTransferCount: number;
  isOngoingCountLoaded: boolean;
  shouldDisplayAdminQueue: boolean;
  shouldDisplayAdminTools: boolean;
  shouldDisplayShowings: boolean;
  fetchQueueCount: () => void;
  fetchOngoingTransferCount: () => void;
  /**
   * Admin functionality can be restricted before the org is fully set up
   * Assuming the org (including answering service/mls staff) will bypass this restriction
   */
  isAdminAccessRestricted: boolean;
  setIsAdminAccessRestricted: React.Dispatch<
    React.SetStateAction<AdminContextType['isAdminAccessRestricted']>
  >;

  /**
   * Provides access to important onboarding portal ids for mls-specific logic
   */
  onboardingPortalIds: {
    TRREB: string;
    MIBOR: string;
    RECO: string;
  };
};

// No longer limited to admin features
const AdminContext = createContext<AdminContextType>(null);

export const AdminContextProvider: React.FC = ({ children }) => {
  const { auth, hasFeature, orgStatus, onboarding } = useContext(AuthContext);

  const [queueCount, setQueueCount] = useState(0);
  const [ongoingTransferCount, setOngoingTransferCount] = useState(0);
  const [isOngoingCountLoaded, setIsOngoingCountLoaded] = useState(false);
  const [isAdminAccessRestricted, setIsAdminAccessRestricted] =
    React.useState(false);

  const [onboardingPortalIds, setOnboardingPortalIds] = React.useState({
    TRREB: '',
    MIBOR: '',
    RECO: '',
  });

  const shouldDisplayAdminQueue = useMemo(() => {
    if (!auth) return false;
    return (
      ((auth.roles[auth.role] === auth.roles.TEAM_ADMIN &&
        hasFeature('TEAMS')) ||
        (auth.roles[auth.role] > auth.roles.TEAM_ADMIN &&
          hasFeature('MY_BROKERAGE'))) &&
      hasFeature('APPOINTMENT') &&
      auth.org.type !== 'mlsStaff'
    );
  }, [auth, hasFeature]);

  const shouldDisplayAdminTools = useMemo(() => Boolean(auth?.isAdmin), [auth]);
  const shouldDisplayShowings = useMemo(
    () => Boolean(auth) && isUserLicensed(auth),
    [auth],
  );

  const shouldRestrictAdminAccess = useMemo(() => {
    const hasNotStartedOnboarding =
      ORG_STATUS_ORDER[orgStatus] < ORG_STATUS_ORDER[OrgStatus.ONBOARDING];
    const hasNotSetupOrg =
      STAGE_ORDER[onboarding?.currentStage.name] <=
      STAGE_ORDER[StageName.WIZARD];
    const isAssumed = Boolean(auth?.assumingUserId || auth?.isAssumedNotBB);

    return (
      auth.org.type === 'brokerage' &&
      !isAssumed &&
      (hasNotStartedOnboarding || hasNotSetupOrg)
    );
  }, [auth, onboarding, orgStatus]);

  const [isLoadingQueueCount, setIsLoadingQueueCount] = useState(false);
  const fetchQueueCount = async () => {
    if (!shouldDisplayAdminQueue) return;

    // Prevent simultaneous requests from causing browser cache breakdown problem
    if (isLoadingQueueCount) return;

    setIsLoadingQueueCount(true);

    // Add random offset to prevent all clients from sending requests at the same time
    await new Promise(resolve => setTimeout(resolve, Math.random() * 500));

    let data;
    if (auth.org.type === 'answering') {
      const response = await axios.get('/answering/appointments/badge_count');
      data = response.data;
    } else {
      const { locations: userLocations, location: userLocation } = auth;

      const locations = Array.from(
        new Set<string>([...(userLocations || []), userLocation]),
      );

      const response = await axios.get('/appointments/badge_count', {
        params: { locations },
      });
      data = response.data;
    }

    setQueueCount(data.queueCount);

    setIsLoadingQueueCount(false);
  };

  const fetchOngoingTransferCount = useCallback(async () => {
    if (!shouldDisplayAdminTools) return;
    const { count } = await getOngoingTransferCount({});
    setOngoingTransferCount(count);
    setIsOngoingCountLoaded(true);
  }, [shouldDisplayAdminTools]);

  useEffect(() => {
    const fetchOnboardingPortalIds = async () => {
      const result = await SecretsController.getOnboardingPortalIds();

      // @ts-expect-error
      setOnboardingPortalIds({ ...result });
    };

    fetchOnboardingPortalIds();
  }, [auth, setOnboardingPortalIds]);

  useEffect(() => {
    setIsAdminAccessRestricted(shouldRestrictAdminAccess);
  }, [shouldRestrictAdminAccess]);

  return (
    <AdminContext.Provider
      value={{
        queueCount,
        ongoingTransferCount,
        isOngoingCountLoaded,
        shouldDisplayAdminQueue,
        shouldDisplayAdminTools,
        shouldDisplayShowings,
        fetchQueueCount,
        fetchOngoingTransferCount,
        isAdminAccessRestricted,
        setIsAdminAccessRestricted,
        onboardingPortalIds,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
